import React, { useRef } from 'react';
import { useLenderPackageImport } from '../../../hooks';
import {
  LenderPackageResultSummary,
  LenderPackageSelectStep,
} from '../../../components/lender-packages';

interface LenderPackageImportModalProps {
  isOpen: boolean;
  lenderId: string;
  lenderName: string;
  onClose: () => void;
}

/**
 * Modal wizard to import lender packages from a CSV for a single, fixed lender.
 * Upload → select packages (existing ones flagged) → confirm replacement → done.
 */
export const LenderPackageImportModal: React.FC<LenderPackageImportModalProps> = ({
  isOpen,
  lenderId,
  lenderName,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importer = useLenderPackageImport();

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    importer.setSelectedFile(event.target.files?.[0] ?? null);
  };

  const handleClose = (): void => {
    importer.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-4xl mx-4">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Import Lender Packages</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Upload CSV data for <span className="font-medium text-slate-700">{lenderName}</span>.
          </p>
        </div>

        <div className="px-6 py-5">
          {importer.step === 'upload' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">CSV File (.csv)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={importer.isLoading}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800
                             file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1
                             file:text-xs file:font-medium file:text-slate-700 disabled:opacity-50"
                />
              </div>
              <button
                onClick={() => void importer.previewPackages(lenderId)}
                disabled={!importer.selectedFile || importer.isLoading}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white
                           shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {importer.isLoading ? 'Previewing...' : 'Preview Packages'}
              </button>
            </div>
          )}

          {importer.step === 'select' && (
            <LenderPackageSelectStep
              packages={importer.packages}
              selectedKeys={importer.selectedKeys}
              selectedExistingPackages={importer.selectedExistingPackages}
              isLoading={importer.isLoading}
              isReplaceWarningOpen={importer.isReplaceWarningOpen}
              onBack={importer.goToUpload}
              onToggleKey={importer.toggleKey}
              onSelectAll={importer.selectAll}
              onDeselectAll={importer.deselectAll}
              onRequestImport={() => importer.requestImport(lenderId)}
              onConfirmReplace={() => void importer.confirmImport(lenderId)}
              onCloseReplaceWarning={importer.closeReplaceWarning}
            />
          )}

          {importer.step === 'done' && importer.result && (
            <LenderPackageResultSummary result={importer.result} />
          )}
        </div>

        <div className="flex items-center justify-end border-t border-slate-200 px-6 py-4">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            {importer.step === 'done' ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};
